import { useState, useEffect, useRef } from "react";
import "@/App.css";
import "@/EditorPanel.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Clock, MapPin, User, Building2, Calendar,
  ChevronRight, DoorOpen, Sofa, UtensilsCrossed, Bed, Bath,
  Droplet, Trees, Sparkles, Car, Plus, Home, Video, Loader2,
  RefreshCw, Copy, ClipboardCheck, Upload, BarChart3, FileText,
  Film, CheckCircle, AlertCircle, TrendingUp, Timer, Mic, Send,
  X, Play, MessageSquare, Users, Clapperboard, Palette
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/AuthContext";
import { AppHeader } from './AppHeader';
import EditorListings from './EditorListings';
import EditorTeam from './EditorTeam';
import EditorSocialUploads from './EditorSocialUploads';
import TemplateStyler from './TemplateStyler';
import { AvatarChip } from './AvatarPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_ICONS = {
  "door-open": DoorOpen, "sofa": Sofa, "utensils-crossed": UtensilsCrossed,
  "bed": Bed, "bath": Bath, "droplet": Droplet, "trees": Trees,
  "sparkles": Sparkles, "car": Car, "map-pin": MapPin, "plus": Plus, "home": Home,
};
const getSectionIcon = (n) => SECTION_ICONS[n] || Home;

function EditorPanel({ onBack, onNavigate }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("listings");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [copiedSection, setCopiedSection] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadOrderRef = useRef(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(id);
    setTimeout(() => setCopiedSection(null), 2000);
    toast.success('Copied to clipboard');
  };

  const copyAllSections = (sections) => {
    const fullText = sections.map(s => s.text).join('\n\n');
    navigator.clipboard.writeText(fullText);
    setCopiedSection('all');
    setTimeout(() => setCopiedSection(null), 2000);
    toast.success('Full script copied to clipboard');
  };

  useEffect(() => {
    if (user && axios.defaults.headers.common['Authorization']) {
      fetchAll();
    }
  }, [user]);

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/orders/list`),
        axios.get(`${API}/orders/stats`),
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 403) toast.error('Editor access required');
      else toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setSelectedOrder(response.data);
    } catch {
      toast.error('Failed to load order details');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setIsUpdating(true);
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      toast.success(`Order marked as ${newStatus}`);
      // Refresh stats
      const statsRes = await axios.get(`${API}/orders/stats`);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleVideoUpload = async (orderId, file) => {
    setUploadingId(orderId);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const res = await axios.post(`${API}/orders/${orderId}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('Video uploaded and delivered!');
      // Refresh
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'Delivered' } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: 'Delivered', video_url: res.data.video_url }));
      }
      const statsRes = await axios.get(`${API}/orders/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const triggerFileUpload = (orderId) => {
    uploadOrderRef.current = orderId;
    fileInputRef.current?.click();
  };

  const onFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (file && uploadOrderRef.current) {
      handleVideoUpload(uploadOrderRef.current, file);
    }
    e.target.value = '';
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'status-pending';
      case 'In Production': return 'status-production';
      case 'Delivered': return 'status-delivered';
      default: return '';
    }
  };

  const getNextStatus = (s) => {
    if (s === 'Pending') return 'In Production';
    if (s === 'In Production') return null; // Upload video instead
    return null;
  };

  const pendingOrders = orders.filter(o => o.status === 'Pending');
  const productionOrders = orders.filter(o => o.status === 'In Production');
  const deliveredOrders = orders.filter(o => o.status === 'Delivered');

  // Hidden file input for video uploads
  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="video/*"
      style={{ display: 'none' }}
      onChange={onFileSelected}
    />
  );

  // ─── Order Detail View ───
  if (selectedOrder) {
    const isMarketUpdate = selectedOrder.order_type === 'market_update';
    return (
      <div className="App">
        <div className="container">
          {hiddenFileInput}
          <AppHeader onBack={() => setSelectedOrder(null)} backLabel="Back to Orders" />
          <div className="order-detail-content">
            <div className="order-info-card">
              <div className="order-info-header">
                <div className="order-title-group">
                  <div className="order-type-badge" data-type={selectedOrder.order_type}>
                    {isMarketUpdate ? <Mic size={14} /> : <Home size={14} />}
                    {isMarketUpdate ? 'Market Update' : 'Property Tour'}
                  </div>
                  <h2 className="order-property-title">{selectedOrder.property_title}</h2>
                  <div className={`order-status-badge ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </div>
                </div>
                <div className="order-action-buttons">
                  {getNextStatus(selectedOrder.status) && (
                    <Button
                      onClick={() => updateOrderStatus(selectedOrder.id, getNextStatus(selectedOrder.status))}
                      disabled={isUpdating}
                      className="status-action-btn"
                      data-testid="order-status-btn"
                    >
                      {isUpdating ? <Loader2 className="animate-spin" size={16} /> : <>Accept & Start Production</>}
                    </Button>
                  )}
                  {selectedOrder.status === 'In Production' && (
                    <Button
                      onClick={() => triggerFileUpload(selectedOrder.id)}
                      disabled={uploadingId === selectedOrder.id}
                      className="upload-video-btn"
                      data-testid="order-upload-btn"
                    >
                      {uploadingId === selectedOrder.id ? (
                        <><Loader2 className="animate-spin" size={16} /> Uploading...</>
                      ) : (
                        <><Upload size={16} /> Upload Finished Video</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
              <div className="order-meta-row">
                <div className="order-meta-item"><User size={14} /><span className="meta-value">{selectedOrder.agent_name}</span></div>
                <div className="order-meta-item"><Building2 size={14} /><span className="meta-value">{selectedOrder.agency_name}</span></div>
                <div className="order-meta-item"><MapPin size={14} /><span className="meta-value">{selectedOrder.town}</span></div>
                <div className="order-meta-item"><Calendar size={14} /><span className="meta-value">{formatDate(selectedOrder.submitted_at)}</span></div>
                {selectedOrder.wms_order_id && (
                  <div className="order-meta-item" data-testid="order-wms-id" title={selectedOrder.wms_address || ''}>
                    <Building2 size={14} /><span className="meta-value">WMS #{selectedOrder.wms_order_id}</span>
                  </div>
                )}
                {selectedOrder.avatar && (
                  <div className="order-meta-item" data-testid="order-avatar">
                    <span className="meta-value" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Presenter: <AvatarChip avatar={selectedOrder.avatar} size={22} /></span>
                  </div>
                )}
              </div>
            </div>

            {/* Delivered video preview */}
            {selectedOrder.video_url && (
              <div className="order-video-preview" data-testid="order-video-preview">
                <h3><Film size={16} /> Delivered Video</h3>
                <video src={selectedOrder.video_url} controls className="order-video-player" />
              </div>
            )}

            {/* Script content */}
            {isMarketUpdate ? (
              <div className="script-sections-editor">
                <h3 className="sections-title"><Mic size={16} /> Market Update Script</h3>
                <div className="editor-section-card">
                  <div className="section-card-header">
                    <div className="section-card-icon"><Mic size={22} /></div>
                    <h4 className="section-card-title">Script</h4>
                    <button
                      className="section-copy-btn"
                      onClick={() => copyToClipboard(selectedOrder.script, 'script')}
                    >
                      {copiedSection === 'script' ? <ClipboardCheck size={15} /> : <Copy size={15} />}
                    </button>
                  </div>
                  <div className="section-card-content">
                    <p className="section-text" style={{ whiteSpace: 'pre-wrap' }}>{selectedOrder.script}</p>
                  </div>
                </div>

                {selectedOrder.caption && (
                  <div className="editor-section-card mu-caption-card">
                    <div className="section-card-header">
                      <div className="section-card-icon"><MessageSquare size={22} /></div>
                      <h4 className="section-card-title">Social Caption</h4>
                      <button
                        className="section-copy-btn"
                        onClick={() => copyToClipboard(selectedOrder.caption, 'caption')}
                      >
                        {copiedSection === 'caption' ? <ClipboardCheck size={15} /> : <Copy size={15} />}
                      </button>
                    </div>
                    <div className="section-card-content">
                      <p className="section-text">{selectedOrder.caption}</p>
                    </div>
                  </div>
                )}

                {selectedOrder.alt_hooks?.length > 0 && (
                  <div className="editor-section-card">
                    <h4 className="section-card-title" style={{ padding: '0.75rem 1rem 0' }}>Alt Hooks</h4>
                    <div className="section-card-content">
                      {selectedOrder.alt_hooks.map((h, i) => <p key={i} className="section-text alt-item">{h}</p>)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="script-sections-editor">
                <h3 className="sections-title">Script Sections</h3>
                {selectedOrder.sections?.map((section, sIdx) => {
                  const IconComponent = getSectionIcon(section.icon);
                  const sectionId = `section-${sIdx + 1}`;
                  const runningTotal = selectedOrder.sections
                    .slice(0, sIdx + 1)
                    .reduce((sum, s) => sum + (Number(s.duration) || 0), 0);
                  return (
                    <div key={sIdx} className="editor-section-card">
                      <div className="timing-guide">
                        <Clock size={14} /><span>Edit timing guide: ~{Math.round(section.duration)}s of video</span>
                        <span className="timing-guide-total" data-testid={`timing-total-${sIdx}`}>
                          Total: ~{Math.round(runningTotal)}s
                        </span>
                      </div>
                      <div className="section-card-header">
                        <div className="section-card-icon"><IconComponent size={22} /></div>
                        <h4 className="section-card-title">{section.name}</h4>
                        <button className="section-copy-btn" onClick={() => copyToClipboard(section.text, sectionId)}>
                          {copiedSection === sectionId ? <ClipboardCheck size={15} /> : <Copy size={15} />}
                        </button>
                      </div>
                      <div className="section-card-content">
                        <p className="section-text">{section.text}</p>
                        <span className="section-id-badge-corner">{sectionId}</span>
                      </div>
                    </div>
                  );
                })}
                {selectedOrder.sections?.length > 0 && (
                  <button className="copy-all-btn" onClick={() => copyAllSections(selectedOrder.sections)}>
                    {copiedSection === 'all' ? <ClipboardCheck size={16} /> : <Copy size={16} />}
                    {copiedSection === 'all' ? 'Copied!' : 'Copy Full Script'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Dashboard View ───
  return (
    <div className="App">
      <div className="container">
        {hiddenFileInput}
        <AppHeader onBack={onBack} />

        <div className="editor-content">
          {/* Tab Navigation */}
          <div className="editor-tabs" data-testid="editor-tabs">
            {[
              { key: 'listings', label: 'My Listings', icon: Clapperboard },
              ...(user?.editor_admin || user?.role === 'super_admin' ? [{ key: 'team', label: 'Team', icon: Users }] : []),
              { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { key: 'pending', label: `Scripts`, icon: FileText, count: pendingOrders.length },
              { key: 'processing', label: `Processing`, icon: Film, count: productionOrders.length },
              { key: 'delivered', label: `Delivered`, icon: CheckCircle, count: deliveredOrders.length },
              { key: 'social', label: 'Social Library', icon: Send },
              ...(user?.template_styler_access || user?.role === 'super_admin' ? [{ key: 'styler', label: 'Template Styler', icon: Palette }] : []),
            ].map(t => (
              <button
                key={t.key}
                className={`editor-tab ${tab === t.key ? 'active' : ''}`}
                onClick={() => setTab(t.key)}
                data-testid={`editor-tab-${t.key}`}
              >
                <t.icon size={16} />
                {t.label}
                {t.count !== undefined && t.count > 0 && <span className="tab-count">{t.count}</span>}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <Button variant="outline" size="sm" onClick={fetchAll} className="refresh-btn" data-testid="editor-refresh">
              <RefreshCw size={16} /> Refresh
            </Button>
          </div>

          {isLoading ? (
            <div className="loading-state"><Loader2 className="animate-spin loading-icon" /><p>Loading...</p></div>
          ) : (
            <>
              {/* ── My Listings (WMS-assigned) Tab ── */}
              {tab === 'listings' && (
                <EditorListings onNavigate={onNavigate} />
              )}

              {/* ── Team Overview Tab (editor admin only) ── */}
              {tab === 'team' && (user?.editor_admin || user?.role === 'super_admin') && (
                <EditorTeam onNavigate={onNavigate} />
              )}

              {/* ── Dashboard Tab ── */}
              {tab === 'dashboard' && stats && (
                <div className="editor-dashboard" data-testid="editor-dashboard">
                  <div className="stats-grid">
                    <div className="stat-card stat-pending">
                      <div className="stat-icon"><AlertCircle size={24} /></div>
                      <div className="stat-info">
                        <span className="stat-value">{stats.pending}</span>
                        <span className="stat-label">Pending Scripts</span>
                      </div>
                    </div>
                    <div className="stat-card stat-production">
                      <div className="stat-icon"><Film size={24} /></div>
                      <div className="stat-info">
                        <span className="stat-value">{stats.in_production}</span>
                        <span className="stat-label">In Production</span>
                      </div>
                    </div>
                    <div className="stat-card stat-delivered-card">
                      <div className="stat-icon"><CheckCircle size={24} /></div>
                      <div className="stat-info">
                        <span className="stat-value">{stats.delivered_this_month}</span>
                        <span className="stat-label">Delivered This Month</span>
                      </div>
                    </div>
                    <div className="stat-card stat-turnaround">
                      <div className="stat-icon"><Timer size={24} /></div>
                      <div className="stat-info">
                        <span className="stat-value">
                          {stats.avg_turnaround_hours != null
                            ? stats.avg_turnaround_hours < 24
                              ? `${stats.avg_turnaround_hours}h`
                              : `${(stats.avg_turnaround_hours / 24).toFixed(1)}d`
                            : '—'}
                        </span>
                        <span className="stat-label">Avg Turnaround</span>
                      </div>
                    </div>
                  </div>

                  <div className="stats-row-2">
                    <div className="stat-card stat-wide">
                      <div className="stat-icon"><TrendingUp size={24} /></div>
                      <div className="stat-info">
                        <span className="stat-value">{stats.total_orders}</span>
                        <span className="stat-label">Total Orders</span>
                      </div>
                      <div className="stat-breakdown">
                        <span><Home size={13} /> {stats.tour_scripts} Tours</span>
                        <span><Mic size={13} /> {stats.market_updates} Market Updates</span>
                      </div>
                    </div>
                    <div className="stat-card stat-wide">
                      <div className="stat-icon"><CheckCircle size={24} /></div>
                      <div className="stat-info">
                        <span className="stat-value">{stats.delivered_total}</span>
                        <span className="stat-label">All Time Delivered</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Scripts (Pending) Tab ── */}
              {tab === 'pending' && (
                <OrdersTable
                  orders={pendingOrders}
                  emptyMsg="No pending scripts"
                  onSelect={fetchOrderDetails}
                  formatDate={formatDate}
                  getStatusColor={getStatusColor}
                />
              )}

              {/* ── Processing Tab ── */}
              {tab === 'processing' && (
                productionOrders.length === 0 ? (
                  <div className="empty-state"><Film size={24} /><p>No videos in production</p></div>
                ) : (
                  <div className="processing-grid" data-testid="processing-grid">
                    {productionOrders.map(order => (
                      <div key={order.id} className="processing-card" data-testid={`processing-${order.id}`}>
                        <div className="processing-card-header">
                          <div className="order-type-badge" data-type={order.order_type}>
                            {order.order_type === 'market_update' ? <Mic size={12} /> : <Home size={12} />}
                            {order.order_type === 'market_update' ? 'Market Update' : 'Tour'}
                          </div>
                          <span className="processing-date">{formatDate(order.submitted_at)}</span>
                        </div>
                        <h4 className="processing-title">{order.property_title}</h4>
                        <div className="processing-meta">
                          <span><User size={12} /> {order.agent_name}</span>
                          <span><Building2 size={12} /> {order.agency_name}</span>
                        </div>
                        <div className="processing-actions">
                          <button className="processing-view-btn" onClick={() => fetchOrderDetails(order.id)}>
                            <FileText size={14} /> View Script
                          </button>
                          <button
                            className="processing-upload-btn"
                            onClick={() => triggerFileUpload(order.id)}
                            disabled={uploadingId === order.id}
                            data-testid={`upload-btn-${order.id}`}
                          >
                            {uploadingId === order.id ? (
                              <><Loader2 size={14} className="animate-spin" /> Uploading...</>
                            ) : (
                              <><Upload size={14} /> Upload Video</>
                            )}
                          </button>
                        </div>
                        {order.has_caption && (
                          <div className="processing-caption-badge"><MessageSquare size={11} /> Caption linked</div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* ── Delivered Tab ── */}
              {tab === 'delivered' && (
                <OrdersTable
                  orders={deliveredOrders}
                  emptyMsg="No delivered orders yet"
                  onSelect={fetchOrderDetails}
                  formatDate={formatDate}
                  getStatusColor={getStatusColor}
                />
              )}

              {/* ── Social Library Uploads Tab ── */}
              {tab === 'social' && <EditorSocialUploads />}
              {tab === 'styler' && (user?.template_styler_access || user?.role === 'super_admin') && <TemplateStyler />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reusable Orders Table ──
function OrdersTable({ orders, emptyMsg, onSelect, formatDate, getStatusColor }) {
  if (orders.length === 0) {
    return <div className="empty-state"><FileText size={24} /><p>{emptyMsg}</p></div>;
  }
  return (
    <div className="orders-list" data-testid="orders-table">
      <div className="orders-table-header">
        <span className="col-type">Type</span>
        <span className="col-property">Property / Area</span>
        <span className="col-agent">Agent</span>
        <span className="col-agency">Agency</span>
        <span className="col-date">Submitted</span>
        <span className="col-status">Status</span>
        <span className="col-action"></span>
      </div>
      {orders.map(order => (
        <div key={order.id} className="order-row" onClick={() => onSelect(order.id)} data-testid={`order-${order.id}`}>
          <span className="col-type">
            <span className="order-type-badge small" data-type={order.order_type}>
              {order.order_type === 'market_update' ? <Mic size={11} /> : <Home size={11} />}
            </span>
          </span>
          <span className="col-property">{order.property_title}</span>
          <span className="col-agent"><User size={14} />{order.agent_name}</span>
          <span className="col-agency"><Building2 size={14} />{order.agency_name}</span>
          <span className="col-date"><Calendar size={14} />{formatDate(order.submitted_at)}</span>
          <span className={`col-status ${getStatusColor(order.status)}`}>{order.status}</span>
          <span className="col-action"><ChevronRight size={18} /></span>
        </div>
      ))}
    </div>
  );
}

export default EditorPanel;
